import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AddButton from '../../component/AddButton';
import AddUser from '../../component/Case/AddUser';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DataTable from '../../component/DataTable';
import SwitchButton from '../../component/SwitchButton';
import { get, patch, post } from '../../services/api-services';
import ModuleHeading from '../../component/ModuleHeading';
import { useCommon } from '../../context/CommonContext';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { formatDatecase, formatFullDate, getCurrentDateTime } from '../../modules/ConvertDate';
import StatusDialog from '../../dialogs/StatusDialog';
import debounce from 'lodash/debounce';
import AddWeight from '../../component/Case-Detail/AddWeight';
import { useSearchParams } from 'react-router-dom';

const caseCache = {};

const Case = () => {
        const [maxHeight, setMaxHeight] = useState(window.innerHeight - 115);

    const [openUserDialog, setOpenUserDialog] = useState(false);
    const [openStatus, setOpenStatus] = useState(false);

    const [allPrograms, setAllPrograms] = useState([]);
    const [openWeightDialog, setOpenWeightDialog] = useState(false);

    // --- URL query params for search state persistence ---
    const [searchParams, setSearchParams] = useSearchParams();
    const initialSearch = searchParams.get('q') || '';
    const initialPage = parseInt(searchParams.get('page') || '0', 10);
    const initialPageSize = parseInt(searchParams.get('pageSize') || '15', 10);
    const initialProgram = searchParams.get('program') || 'All';

    // Check cache synchronously for initial state
    const initialCacheKey = `${initialPage}-${initialPageSize}-${initialSearch}-${initialProgram}`;
    const [enrollments, setEnrollments] = useState(caseCache[initialCacheKey]?.data || []);
    const [rowCount, setRowCount] = useState(caseCache[initialCacheKey]?.total || 0);
    const [loading, setLoading] = useState(!caseCache[initialCacheKey]);

    const [paginationModel, setPaginationModel] = useState({ pageSize: initialPageSize, page: initialPage });
    const [userId, setUserId] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState(initialProgram);
    const [search, setSearch] = useState(initialSearch);
    const [statusData, setStatusData] = useState({});
 
    const [weight, setWeight] = useState({ type: 'weight', value: "", userComment: "", unit: 'Kgs', recordedAt: getCurrentDateTime(), createdAt: new Date(), updatedAt: new Date(), customerId: '' })
    const [userData, setUserData] = useState({
        mobile: '',
        name: '',
        email: ''
    });
    const { currentUser, setSnackbar } = useCommon();

    // Sync state to URL query params (debounced to avoid excessive URL updates)
    const urlDebounceRef = useRef(null);
    useEffect(() => {
        if (urlDebounceRef.current) clearTimeout(urlDebounceRef.current);
        urlDebounceRef.current = setTimeout(() => {
            const params = {};
            if (search) params.q = search;
            if (paginationModel.page !== 0) params.page = String(paginationModel.page);
            if (paginationModel.pageSize !== 15) params.pageSize = String(paginationModel.pageSize);
            if (selectedProgram && selectedProgram !== 'All') params.program = selectedProgram;
            setSearchParams(params, { replace: true });
        }, 500);
        return () => clearTimeout(urlDebounceRef.current);
    }, [search, paginationModel, selectedProgram, setSearchParams]);

    const onClickStatus = (user) => {
        setStatusData(user)
        setOpenStatus(true);
    }

    const columns = [
        {
            field: 'no',
            headerName: 'No',
            width: 60,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                if (params?.id && params?.api) {
                    const sortedRowIds = params?.api.getSortedRowIds();
                    const rowIndex = sortedRowIds.indexOf(params?.id);
                    return <div className='flex gap-2 items-center'>{paginationModel.page * paginationModel.pageSize + rowIndex + 1}</div>;
                }
                return null;
            }
        },
        {
            field: 'name',
            headerName: 'User Name',
            minWidth: 150,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center truncate' >
                    {params?.row?.name}

                </div>
            }

        },
        {
            field: 'mobile',
            headerName: 'Mobile',
            minWidth: 120,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center' >
                    {params?.row?.mobile}
                </div>

            }
        },
        {
            field: 'email',
            headerName: 'Email',
            minWidth: 200,
            flex: 1,
            priority: 'low',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                const email = params?.row?.email || '';
                const maxLength = 30;
                return (
                    <Tooltip title={email}>
                        <div
                            className="truncate max-w-[300px] cursor-pointer"
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {email.length > maxLength ? `${email.slice(0, maxLength)}...` : email}
                        </div>
                    </Tooltip>
                );
            }
        },
        {
            field: 'Enrolled',
            headerName: 'Enrolled Programs',
            minWidth: 120,
            flex: 1,
            priority: 'medium',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                const programTitles = params?.row?.programTitle || [];
                const firstTitle = programTitles[0] || '';
                const remainingCount = programTitles.length - 1;

                if (!programTitles.length) {
                    return <div className="flex gap-2 items-center">N/A</div>;
                }

                return (
                    <div className="flex gap-2 items-center">
                        {firstTitle}
                        {remainingCount > 0 && (
                            <Tooltip title={programTitles.slice(1).join(', ')}>
                                <Typography color='secondary'>+{remainingCount}</Typography>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },

        {
            field: 'registered',
            headerName: 'Registered On',
            minWidth: 120,
            flex: 1,
            priority: 'medium',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center' >
                    {formatDatecase(params?.row?.createdAt)}
                </div>
            }
        },
        {
            field: 'enrolledDate',
            headerName: 'Enrollment Date',
            minWidth: 120,
            flex: 1,
            priority: 'medium',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <div className='flex gap-2  items-center' >
                    {params?.row?.enrollDate?.length > 0 ? formatFullDate(params?.row?.enrollDate[params?.row?.enrollDate?.length - 1]) : 'Not Enroll Yet'}
                    {/* <DateShow date={latestEnrollDate} /> */}
                </div>
            }
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 120,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                const isActive = params.row.status === "active";
                return (
                    <div className="flex gap-2 items-center">

                        <SwitchButton color='secondary.main' isActive={isActive} status={params?.row?.status} onClick={() => onClickStatus(params?.row)} />
                        <span className=' capitalize'>{params.row.status}</span>
                    </div>
                );
            }
        },
        {
            field: 'editUser',
            headerName: 'Edit User',
            minWidth: 80,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <IconButton
                    size='small'
                    onClick={(e) => {
                        e.stopPropagation();
                        onUpdateUser(params?.row)
                    }}>
                    <EditIcon color="primary" />
                </IconButton>
            }
        },
        {
            field: 'addWeight',
            headerName: 'Add Weight',
            minWidth: 120,
            headerClassName: 'super-app-theme--header',
            sortable: false,
            renderCell: (params) => {
                return <IconButton size='small'
                    //  onClick={(e) => {e.stopPropagation();
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenWeightDialog(true);
                        setWeight((prev) => ({ ...prev, customerId: params?.row?._id }))
                    }}>
                    <AddIcon color='secondary'
                    />
                </IconButton>
            }
        },
    ];

    const onOpenUser = () => {
        setOpenUserDialog(true);
    }
    const handleChange = (event) => {
        const value = event.target.value;
        setSelectedProgram(value);
        // Reset to page 0 — batches with setSelectedProgram into ONE render
        setPaginationModel(prev => ({ ...prev, page: 0 }));
    };
    const onUpdateUser = (user) => {
        setUserId(user._id);
        setOpenUserDialog(true)
        setUserData({
            mobile: user?.mobile,
            name: user?.name,
            email: user?.email
        })
    }
    const handleChangeStatus = async () => {
        const response = await patch(`/Customers/${statusData._id}`, { status: statusData?.status === 'active' ? 'inactive' : 'active' });
        if (response?.statusCode === 200) {
            setSnackbar({
                openSnackbar: true,
                message: 'Status updated successfully',
                severity: 'success'
            })
            caseDelay(currentUser, paginationModel, search, selectedProgram);
            setOpenStatus(false);
        } else {
            setSnackbar({
                openSnackbar: true,
                message: 'Something went wrong!',
                severity: 'error'
            })
        }
    }
    const fetchPrograms = useCallback(async () => {
        try {
            const response = await get("/Programs");
            if (response?.statusCode !== 200) {
                throw new Error(`Failed to fetch programs. Status: ${response?.status}`);
            }
            const data = response.data;
            setAllPrograms(data);
        } catch (error) {
            console.error("Error fetching programs:", error);
        }
        // NOTE: do NOT call setLoading(false) here — onGetCases manages the loading state.
        // Calling setLoading(false) here would flash "No users yet" before cases are fetched.
    }, []);

    const onGetCases = useCallback(async (currentUser, paginationModel, search, selectedProgram) => {
        if (!currentUser?.userId) return;  // wait until user is logged in (userId is the actual nullable field)
        
        const cacheKey = `${paginationModel.page}-${paginationModel.pageSize}-${search}-${selectedProgram}`;
        if (caseCache[cacheKey]) {
            setEnrollments(caseCache[cacheKey].data);
            setRowCount(caseCache[cacheKey].total);
            setLoading(false);
        } else {
            setLoading(true);
        }

        try {
            const url = `/GuestMeasures/getCases?userId=${currentUser?.userId}`;
            const res = await post(url, {
                page: paginationModel,
                userId: currentUser?.userId,
                search: search,
                program: selectedProgram?.includes('All') || !selectedProgram ? [] : [selectedProgram],
                limit: paginationModel?.pageSize,
                skip: paginationModel?.page
            });

            if (res?.statusCode === 200) {
                // Response structure:
                // post() returns { statusCode: HTTPstatus, data: backendBody }
                // backendBody = { statusCode: 200, code: "OK", data: { data: [...], filterCount: N } }
                const payload = res?.data?.data;   // { data: [...], filterCount: N }
                const fetchedData = Array.isArray(payload?.data) ? payload.data : [];
                const fetchedTotal = payload?.filterCount ?? 0;
                setEnrollments(fetchedData);
                setRowCount(fetchedTotal);
                caseCache[cacheKey] = { data: fetchedData, total: fetchedTotal };
                setLoading(false);
            } else {
                setLoading(false);
            }

        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    }, [])

    const caseDelay = useMemo(() => debounce((...args) => onGetCases(...args), 300), [onGetCases]);

    // Single unified effect — all deps watched together to avoid stale-closure bugs.
    // caseDelay (debounced 300ms) handles rapid typing; for pagination/filter changes 300ms is negligible.
    useEffect(() => {
        caseDelay(currentUser, paginationModel, search, selectedProgram);
        return () => caseDelay.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, paginationModel, selectedProgram, currentUser]);

    useEffect(() => {
        fetchPrograms();
    }, [fetchPrograms]);

    useEffect(() => {
        const updateMaxHeight = () => {
            const height = window.innerHeight - 115;
            setMaxHeight(height);
        };
        updateMaxHeight();
        window.addEventListener('resize', updateMaxHeight);
        return () => {
            window.removeEventListener('resize', updateMaxHeight);
        };
    }, []);
    return (
        <div className='flex-1 flex flex-col gap-3 min-h-0'>
                <DataTable
                    headerContent={
                        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4 md:items-center w-full justify-between">
                            <ModuleHeading title="Cases" navigateTo="/admin/dashboard" />
                            <div className="flex space-x-4 md:items-center">
                                <TextField
                                    sx={{ minWidth: '250px' }}
                                    id="standard-basic"
                                    label="Search"
                                    variant="standard"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        // Reset to page 0 — batches with setSearch into ONE render → ONE effect call
                                        setPaginationModel(prev => ({ ...prev, page: 0 }));
                                    }}
                                />
                                <FormControl variant="standard" sx={{ minWidth: '200px' }}>
                                    <InputLabel id="demo-simple-select-standard-label">Enrolled Programs</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-standard-label"
                                        id="demo-simple-select-standard"
                                        value={selectedProgram || "All"}
                                        onChange={handleChange}
                                        label="Enrolled In"
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        {allPrograms?.map((item) => (
                                            <MenuItem key={item?.id} value={item?.id}>
                                                {item?.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                    }
                    rows={enrollments}
                    rowCount={rowCount}
                    columns={columns}
                    loading={loading}
                    tableId="cases"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    navigate={`caseDetails`}
                    emptyMessage="No users found"
                    emptySubMessage="Try adjusting your search or filter criteria"
                />

            <AddButton onOpen={onOpenUser} />
            <StatusDialog open={openStatus} setOpen={setOpenStatus} onSubmit={handleChangeStatus} status={statusData?.status} name="User" />
            <AddUser
                open={openUserDialog}
                setOpen={setOpenUserDialog}
                onGetCases={caseDelay}
                userData={userData}
                setUserData={setUserData}
                userId={userId}
                setUserId={setUserId}
                paginationModel={paginationModel}
                search={search}
                selectedProgram={selectedProgram}
                GetCases={onGetCases}
            />
            <AddWeight
                openAddWeight={openWeightDialog}
                setOpenAddWeight={setOpenWeightDialog}
                weight={weight}
                setWeight={setWeight}
                source={"casePage"}
                userData={userData}
            />
        </div>

    );
};

export default Case;